import { Folder } from '@/api/generatedApi';
import { useMoveFolder } from '@/hooks/api/folders/useMoveFolder';
import { useGetFolders } from '@/hooks/useGetFolders';
import { useToast } from '@/hooks/useToast';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { FolderTree } from './FolderTree';
import { buildFolderTree } from '@/utils/buildFolderTree';
import { getDescendantIds } from './moveFolderUtils';

export const MoveFolderModal = ({
  isOpen,
  onClose,
  folder,
  preselectedTargetFolderId,
  onMoved,
}: {
  isOpen: boolean;
  onClose: () => void;
  folder?: Folder;
  preselectedTargetFolderId?: string | null;
  onMoved?: () => void;
}) => {
  const { showToast } = useToast();
  const { folders, isLoading: isFoldersLoading } = useGetFolders({ enabled: isOpen });

  const initialSelectedFolderId = useMemo(() => {
    if (!folder) return;
    if (preselectedTargetFolderId === null) return;
    if (preselectedTargetFolderId !== undefined) return preselectedTargetFolderId;
    return folder.parentFolderId ?? undefined;
  }, [folder, preselectedTargetFolderId]);

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(initialSelectedFolderId);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && folder) {
      setSelectedFolderId(initialSelectedFolderId);
      setErrorMessage(undefined);
      setExpandedItems(folders.map(f => f.id));
    }
  }, [isOpen, folder?.id, initialSelectedFolderId, folders]);

  const { moveFolder, isMoving } = useMoveFolder();

  const disabledFolderIds = useMemo(() => {
    if (!folder) return new Set<string>();
    const descendants = getDescendantIds(folder.id, folders);
    descendants.add(folder.id);
    return descendants;
  }, [folder, folders]);

  const folderTree = useMemo(() => buildFolderTree(folders, { sort: true }), [folders]);

  const handleSelect = (_event: React.SyntheticEvent | null, itemId: string | null) => {
    if (itemId === 'root') {
      setSelectedFolderId(undefined);
    } else if (itemId && !disabledFolderIds.has(itemId)) {
      setSelectedFolderId(itemId);
    }
  };

  const handleSubmit = () => {
    if (!folder) return;

    const currentIsRoot = folder.parentFolderId === undefined || folder.parentFolderId === null;
    const targetIsRoot = selectedFolderId === undefined;

    if (targetIsRoot && currentIsRoot) {
      setErrorMessage('Folder is already in this location.');
      return;
    }
    if (selectedFolderId === folder.parentFolderId) {
      setErrorMessage('Folder is already in this location.');
      return;
    }

    setErrorMessage(undefined);

    const originalParentFolderId = folder.parentFolderId;
    const newParentFolderId = selectedFolderId;

    moveFolder(
      { folder, parentFolderId: newParentFolderId },
      {
        onSuccess: () => {
          showToast({
            message: `Moved "${folder.name}"`,
            type: 'success',
            action: {
              label: 'Undo',
              onClick: () => {
                moveFolder({
                  folder: { ...folder, parentFolderId: newParentFolderId },
                  parentFolderId: originalParentFolderId,
                });
              },
            },
          });
          onMoved?.();
          onClose();
        },
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { message?: string } } };
          setErrorMessage(axiosError.response?.data?.message ?? 'Failed to move folder');
        },
      }
    );
  };

  const handleClose = () => {
    if (!isMoving) {
      onClose();
    }
  };

  const currentIsRoot = folder?.parentFolderId === undefined || folder?.parentFolderId === null;
  const targetIsRoot = selectedFolderId === undefined;
  const isSameLocation = (targetIsRoot && currentIsRoot) || selectedFolderId === folder?.parentFolderId;
  const canSubmit = !isMoving && !isSameLocation;

  const getDestinationName = () => {
    if (selectedFolderId === undefined) return 'Assets home folder';
    return folders.find(f => f.id === selectedFolderId)?.name ?? 'Unknown';
  };

  const isConfirmationMode = preselectedTargetFolderId !== undefined;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>Move Folder</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          '&:first-of-type': {
            paddingTop: 3,
          },
          ...(!isConfirmationMode && {
            minHeight: 300,
          }),
        }}
      >
        {errorMessage && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {isConfirmationMode ? (
          <Box>
            <Typography>
              Move <strong>{folder?.name}</strong> to <strong>{getDestinationName()}</strong>?
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Select a destination for <strong>{folder?.name}</strong>
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <FolderTree
                folderTree={folderTree}
                selectedFolderId={selectedFolderId}
                expandedItems={expandedItems}
                disabledFolderIds={disabledFolderIds}
                isLoading={isFoldersLoading}
                onSelect={handleSelect}
                onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isMoving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!canSubmit} sx={{ width: '100px' }}>
          {isMoving ? <CircularProgress size={20} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
